export const initialGroupCallState = {
    phase: 'idle',
    intent: 'audio',
    minimized: false,
    full: false,
    lobbyParticipants: [],
    participants: [],
    encryption: { status: 'pending', missingKeys: [], unencrypted: [], unmapped: [], settling: [] },
    local: { microphoneEnabled: false, cameraEnabled: false, screenSharing: false },
};
const ALLOWED_TRANSITIONS = {
    idle: ['lobby', 'joining', 'failed'],
    lobby: ['joining', 'idle', 'failed', 'ended'],
    joining: ['joined', 'failed', 'leaving', 'ended'],
    joined: ['reconnecting', 'leaving', 'failed', 'ended'],
    reconnecting: ['joined', 'leaving', 'failed', 'ended'],
    leaving: ['ended', 'failed'],
    ended: ['idle', 'lobby', 'joining'],
    failed: ['idle', 'lobby', 'joining', 'ended'],
};
export const canTransition = (from, to) => from === to || ALLOWED_TRANSITIONS[from].includes(to);
export const isActivePhase = (phase) => phase === 'joining' || phase === 'joined' || phase === 'reconnecting' || phase === 'leaving';
export const mergeParticipants = (matrix, media, keyed, raisedHands) => {
    const mediaByIdentity = new Map(media.map(snapshot => [snapshot.identity, snapshot]));
    const matrixIdentities = new Set(matrix.map(participant => participant.livekitIdentity));
    const views = matrix.map((participant) => {
        const snapshot = mediaByIdentity.get(participant.livekitIdentity);
        return {
            identity: participant.livekitIdentity,
            userId: participant.userId,
            deviceId: participant.deviceId,
            isLocal: participant.isLocal,
            inMedia: !!snapshot,
            hasKey: participant.isLocal || keyed.has(participant.livekitIdentity),
            encrypted: snapshot?.encrypted ?? true,
            speaking: snapshot?.speaking ?? false,
            audioLevel: snapshot?.audioLevel ?? 0,
            microphoneEnabled: snapshot?.microphoneEnabled ?? false,
            cameraEnabled: snapshot?.cameraEnabled ?? false,
            screenSharing: snapshot?.screenSharing ?? false,
            connectionQuality: snapshot?.connectionQuality ?? 'unknown',
            handRaised: raisedHands.has(participant.livekitIdentity),
            intent: participant.intent,
            joinedAt: participant.joinedAt,
        };
    });
    const unmapped = media
        .filter(snapshot => !snapshot.isLocal && !matrixIdentities.has(snapshot.identity))
        .map(snapshot => snapshot.identity);
    return { views, unmapped };
};
export const unencryptedIdentities = (views) => views.filter(view => view.inMedia && !view.encrypted).map(view => view.identity);
export const summarizeEncryption = (views, unmapped, settling = new Set()) => {
    const unencrypted = unencryptedIdentities(views);
    const missingKeys = views.filter(view => !view.isLocal && !view.hasKey).map(view => view.identity);
    const suspicious = [...unencrypted, ...unmapped];
    const settlingNow = suspicious.filter(identity => settling.has(identity));
    const status = suspicious.length > settlingNow.length ? 'warning' : missingKeys.length > 0 || settlingNow.length > 0 ? 'pending' : 'ok';
    return { status, missingKeys, unencrypted, unmapped: [...unmapped], settling: settlingNow };
};
export const encryptionReason = (summary) => {
    const settled = (identities) => identities.filter(identity => !summary.settling.includes(identity)).length;
    const unencrypted = settled(summary.unencrypted);
    if (unencrypted > 0) {
        return { reason: 'unencrypted', count: unencrypted };
    }
    const unmapped = settled(summary.unmapped);
    if (unmapped > 0) {
        return { reason: 'unmapped', count: unmapped };
    }
    if (summary.settling.length > 0) {
        return { reason: 'settling', count: summary.settling.length };
    }
    if (summary.missingKeys.length > 0) {
        return { reason: 'missingKeys', count: summary.missingKeys.length };
    }
    return { reason: 'ok', count: 0 };
};
export const orderForStage = (views) => [...views].sort((a, b) => {
    if (a.screenSharing !== b.screenSharing) {
        return a.screenSharing ? -1 : 1;
    }
    if (a.handRaised !== b.handRaised) {
        return a.handRaised ? -1 : 1;
    }
    if (a.isLocal !== b.isLocal) {
        return a.isLocal ? 1 : -1;
    }
    return (a.joinedAt ?? 0) - (b.joinedAt ?? 0);
});
